import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import classnames from 'classnames';
import Head from 'next/head';
import Script from 'next/script';
import { AdvScripts, LiraAdvContainer, LiraRunAdvScript } from '@sbt-web/adv';
import { getMacroOfCategoryID, getVerticalOfCategoryID } from '@sbt-web/category-utils';
import { type AdItem, CategoryID } from '@sbt-web/network/types';
import { ButtonLink } from '@sbt-web/ui';
import { AdItemCardMini } from '@sbt-web/item-card';
import { createSkyscraperSizeConstraints } from '@/client/components/Adv/Lira/createSkyscraperSizeConstraints';
import { errorGoneConfig } from '@/client/components/Adv/Lira/fallbackConfig/desktopErrorConfig';
import { msiteErrorGoneConfig } from '@/client/components/Adv/Lira/fallbackConfig/msiteErrorConfig';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { SkeletonWithAdv } from '@/client/components/Adv/SkeletonWithAdv';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { getCategoryThemeById, Theme } from '@/shared/helpers/Themes';
import ownClasses from './410.module.scss';
const getIllustrationThemeByCategoryId = (categoryId: CategoryID) => {
  const theme = getCategoryThemeById(categoryId);
  switch (theme) {
    case Theme.Blue:
      return 'motori';
    case Theme.Yellow:
      return 'market';
    case Theme.Purple:
      return 'immobili';
    case Theme.Green:
      return 'lavoro';
    default:
      return 'corporate';
  }
};
interface Props {
  categoryId: CategoryID;
  categoryLabel: string;
  categoryDefaultUrl: string;
  items: AdItem[];
}
const ErrorPage = t0 => {
  const $ = _c(49);
  const {
    categoryId,
    categoryLabel,
    categoryDefaultUrl,
    items
  } = t0;
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  let t1;
  if ($[0] !== categoryId) {
    t1 = getVerticalOfCategoryID(categoryId);
    $[0] = categoryId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const vertical = t1;
  let t2;
  if ($[2] !== vertical) {
    t2 = errorGoneConfig(vertical);
    $[2] = vertical;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== vertical) {
    t3 = msiteErrorGoneConfig(vertical);
    $[4] = vertical;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      desktop: t2,
      msite: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const fallbackLiraConfig = t4;
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      id: LiraContainerIds.page410.skyscraperRight,
      enabled: true
    };
    t6 = {
      id: LiraContainerIds.page410.skyscraperLeft,
      enabled: true
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Head><title>Subito.it</title><meta name="robots" content="noindex,follow" /></Head>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AdvScripts Script={Script} config={{
      includeOw: false,
      includeTam: false,
      includeAdSense: true,
      includePrebid: true,
      publicPath: "/static/script"
    }} />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== categoryId || $[14] !== categoryLabel || $[15] !== fallbackLiraConfig || $[16] !== noAdvToggle || $[17] !== optimizelyReady || $[18] !== vertical) {
    t9 = optimizelyReady && noAdvToggle !== "B" && <LiraRunAdvScript vertical={vertical} pageType="errorgone" env={process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT} fallbackHebeConfig={fallbackLiraConfig} targeting={{
      categoryName: categoryLabel,
      macroCategoryId: getMacroOfCategoryID(categoryId).id
    }} constraintsByContainerId={createSkyscraperSizeConstraints(LiraContainerIds.page410.skyscraperLeft, LiraContainerIds.page410.skyscraperRight)} />;
    $[13] = categoryId;
    $[14] = categoryLabel;
    $[15] = fallbackLiraConfig;
    $[16] = noAdvToggle;
    $[17] = optimizelyReady;
    $[18] = vertical;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = classnames(ownClasses["top-level-section"], ownClasses["title-section"]);
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== categoryId) {
    t11 = getIllustrationThemeByCategoryId(categoryId);
    $[21] = categoryId;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = `${process.env.NEXT_PUBLIC_ASSETS_BASE_URL}/static/errors/not-found/${t11}.svg`;
  let t13;
  if ($[23] !== t12) {
    t13 = <img src={t12} alt="" height="150" className={ownClasses["title-image"]} />;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <h1 className="headline-4">Questo annuncio non esiste più</h1>;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="body-text">È stato venduto.<br />Prova a dare un'occhiata ai risultati simili sotto.</p>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className={ownClasses["title-text"]}>{t14}{t15}<ButtonLink href="/annunci-italia/vendita/usato/" classes={[ownClasses["new-search-link"]]}>Effettua una nuova ricerca</ButtonLink></div>;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t13) {
    t17 = <section className={t10}>{t13}{t16}</section>;
    $[28] = t13;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = classnames(ownClasses["top-level-section"], ownClasses["content-section"]);
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== categoryLabel) {
    t19 = <h2 className="headline-5">Altri risultati per {categoryLabel}</h2>;
    $[31] = categoryLabel;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = [ownClasses["specific-listing-link"]];
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== categoryDefaultUrl) {
    t21 = <ButtonLink href={categoryDefaultUrl} classes={t20}>Vedi tutti</ButtonLink>;
    $[34] = categoryDefaultUrl;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t19 || $[37] !== t21) {
    t22 = <header className={ownClasses["content-title"]}>{t19}{t21}</header>;
    $[36] = t19;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== items) {
    t23 = items.length > 0 ? <ul className={ownClasses["card-grid"]}>{items.map(_temp)}</ul> : null;
    $[39] = items;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <LiraAdvContainer idForAd={LiraContainerIds.page410.belowTheFold} className={ownClasses["adv-slot"]} />;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t22 || $[43] !== t23) {
    t25 = <section className={t18}>{t22}{t23}{t24}</section>;
    $[42] = t22;
    $[43] = t23;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t17 || $[46] !== t25 || $[47] !== t9) {
    t26 = <SkeletonWithAdv skyscraperRight={t5} skyscraperLeft={t6}>{t7}{t8}{t9}{t17}{t25}</SkeletonWithAdv>;
    $[45] = t17;
    $[46] = t25;
    $[47] = t9;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  return t26;
};
export default ErrorPage;
export type { Props };
function _temp(item) {
  return <li className={ownClasses["card-grid-item"]} key={item.urn}><AdItemCardMini item={item} assetsBase={process.env.NEXT_PUBLIC_ASSETS_BASE_URL} aspectRatio="4/5" imageSizeType="responsive" /></li>;
}